import { useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import { OneDateNavigator } from '@factorialco/f0-react/dist/experimental'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useEffect } from 'modules/core/hooks/useEffect'
import { stringToDate } from 'modules/core/lib/dates'
import { firstDayOfWeekAsIndex } from 'modules/core/lib/firstDayOfWeek'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import useChangeView from 'modules/shift_management/hooks/useChangeView'
import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import View from 'modules/shift_management/store/internal/models/View'
import {
  GranularityViewType,
  isValidGranularityViewType,
} from 'modules/shift_management/store/internal/models/View'

interface Props {
  hasNoneShiftManagementPlanning: boolean
}

type DateGranularity = {
  granularity: GranularityViewType
  value: { from: Date; to: Date }
}

const DEFAULT_GRANULARITY: GranularityViewType = 'week'

const getGranularityFromViewType = (viewType: string): GranularityViewType => {
  if (viewType === 'daily') return 'day'
  if (viewType === 'weekly') return 'week'
  if (viewType === 'monthly') return 'month'
  if (viewType === 'four-weeks') return 'range'
  return DEFAULT_GRANULARITY
}

const NewThemeHeaderViewNavigator = ({ hasNoneShiftManagementPlanning }: Props) => {
  const { view, onChangePeriod, onChangeRoute } = useChangeView()
  const [{ view: currentView }] = useSchedule()
  const [{ featureFlags }] = useEnabledFeatures()
  const ALL_GRANULARITY_VIEWS: GranularityViewType[] = ['day', 'week', 'range', 'month']

  // Load granularity from localStorage with error handling
  const [storedGranularity, setStoredGranularity] = useLocalStorage<GranularityViewType>(
    LOCAL_STORAGE_KEYS.shiftManagementGranularity,
    getGranularityFromViewType(view.viewType)
  )

  // Validate stored granularity and fallback to default if invalid
  const validStoredGranularity =
    storedGranularity && isValidGranularityViewType(storedGranularity) ? storedGranularity : null

  // Track if we've initialized from localStorage to prevent loops
  const hasInitializedFromStorage = useRef(false)
  const isInternalUpdate = useRef(false)
  // Store stable function references to avoid TypeScript errors in useEffect dependencies
  const onChangeRouteRef = useRef(onChangeRoute)
  const setStoredGranularityRef = useRef(setStoredGranularity)

  // Keep refs in sync with latest function references
  onChangeRouteRef.current = onChangeRoute
  setStoredGranularityRef.current = setStoredGranularity

  const viewGranularity = getGranularityFromViewType(view.viewType)
  const initialGranularity: GranularityViewType = validStoredGranularity || viewGranularity

  const [currentDate, setCurrentDate] = useState<DateGranularity>({
    granularity: initialGranularity,
    value: {
      from: stringToDate(view.startAt, 'backend').toDate(),
      to: stringToDate(view.endAt, 'backend').toDate(),
    },
  })

  const { access: currentUser } = useCurrentSession()

  const { isTimeFormat24H } = useIsTimeFormat24H()

  const onClickSelectNew = ({ granularity, value }: DateGranularity) => {
    // needed return as this function is called on every click of the date picker, but we only want it when the date changes
    if (
      value.from.getTime() === currentDate.value.from.getTime() &&
      granularity === currentDate.granularity
    )
      return
    const selectedView = new View({
      date: moment(value.from),
      viewType: view.viewType,
      groupType: view.groupType,
      isTimeFormat24H,
      featureFlags,
    })
    onChangePeriod(selectedView)

    setCurrentDate({
      granularity: granularity,
      value: { from: value.from, to: value.to },
    })
  }

  // On initial mount, if stored granularity differs from URL, navigate once
  useEffect(() => {
    if (!hasInitializedFromStorage.current) {
      hasInitializedFromStorage.current = true
      const currentViewGranularity = getGranularityFromViewType(view.viewType)
      if (validStoredGranularity && validStoredGranularity !== currentViewGranularity) {
        isInternalUpdate.current = true
        onChangeRouteRef.current(`${validStoredGranularity}/${view.groupType}`)
      }
    }
  }, [validStoredGranularity, view.viewType, view.groupType])

  // Update granularity when view changes externally (e.g., from URL params or navigation)
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false
      return
    }
    const newViewGranularity = getGranularityFromViewType(view.viewType)
    if (newViewGranularity !== currentDate.granularity) {
      setCurrentDate((prev) => ({
        ...prev,
        granularity: newViewGranularity,
      }))
    }
  }, [view.viewType, currentDate.granularity])

  // When switching to areas view, force granularity to week if not already
  // Areas view only supports week granularity
  useEffect(() => {
    if (view.isByAreas && currentDate.granularity !== 'week') {
      setStoredGranularityRef.current('week')
      setCurrentDate((prev) => ({
        ...prev,
        granularity: 'week',
      }))
      isInternalUpdate.current = true
      onChangeRouteRef.current(`week/${view.groupType}`)
    }
  }, [view.isByAreas, currentDate.granularity, view.groupType])

  useUnsafeEffect(() => {
    // Parse as local midnight so sync comparison and stored dates match local calendar (fix Honolulu UTC-midnight shift)
    const viewFromDate = stringToDate(view.startAt, 'backend').toDate()
    const viewToDate = stringToDate(view.endAt, 'backend').toDate()
    // needed return as we update the view from outside this component, but we don't re render the component so we only want to update its value when updated outside it
    if (viewFromDate.getDate() === currentDate.value.from.getDate()) return

    setCurrentDate((prev) => ({
      ...prev,
      value: { from: viewFromDate, to: viewToDate },
    }))
  }, [view.startAt, view.endAt])

  useUnsafeEffect(() => {
    const viewFromDate = stringToDate(currentView.startAt, 'backend').toDate()
    const viewToDate = stringToDate(currentView.endAt, 'backend').toDate()
    if (viewFromDate.getDate() === currentDate.value.from.getDate()) return

    setCurrentDate((prev) => ({
      ...prev,
      value: { from: viewFromDate, to: viewToDate },
    }))
  }, [currentView.startAt, currentView.endAt])

  const onGranularityChange = (granularity: GranularityViewType) => {
    // SafeLocalStorage handles errors internally, so this won't throw
    setStoredGranularity(granularity)

    setCurrentDate((prev) => ({
      ...prev,
      granularity: granularity,
    }))

    isInternalUpdate.current = true
    onChangeRoute(`${granularity}/${view.groupType}`)
  }

  const granularityOptions = ALL_GRANULARITY_VIEWS.map((granularity) => {
    const labelKey =
      granularity === 'day'
        ? i18n.t('shift_management.filters.view_selector.views.daily')
        : granularity === 'week'
          ? i18n.t('shift_management.filters.view_selector.views.weekly')
          : granularity === 'range'
            ? i18n.t('shift_management.filters.view_selector.views.four_weeks')
            : i18n.t('shift_management.filters.view_selector.views.monthly')

    return {
      value: granularity,
      label: labelKey,
      disabled: view.isByAreas && granularity != 'week',
    }
  })

  return (
    <Box flexDirection="row" alignItems="center" gap="s8" minWidth="none">
      <Box flexDirection="row" alignItems="center" gap="s8">
        <F0Select
          value={currentDate.granularity}
          options={granularityOptions}
          onChange={onGranularityChange}
          hideLabel
          label={i18n.t('common.date.date')}
        />
        <OneDateNavigator
          value={{
            granularity: currentDate.granularity,
            value: {
              from: stringToDate(view.startAt, 'backend').toDate(),
              to: stringToDate(view.endAt, 'backend').toDate(),
            },
          }}
          granularities={[currentDate.granularity]}
          weekStartsOn={firstDayOfWeekAsIndex(currentUser.firstDayOfWeek)}
          onSelect={(selected) => {
            const dateRange = selected?.value
            const granularity = selected?.granularity
            if (dateRange && granularity && isValidGranularityViewType(granularity)) {
              onClickSelectNew({
                granularity,
                value: { from: dateRange.from, to: dateRange.to },
              })
            }
          }}
        />
      </Box>
    </Box>
  )
}

export default observer(NewThemeHeaderViewNavigator)
